import { memo, useState } from "react";
import { ChevronDown, ChevronRight, FileText, Lock } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

import type { RagCitation, TurnRagBatch } from "../../domain/types";
import { Pill } from "../common/Pill";
import { cn } from "../common/cn";

/**
 * RagCitationsSubcard — live transcript surface for RAG snippets.
 *
 * The orchestrator emits a `turn.rag` SSE event with the structured
 * `RagCitation[]` payload that the speaker grounded their answer on.
 * The expert's prose deliberately does NOT mention filenames (that is
 * UX noise for a defense-industry audience and gets dropped by the
 * prompt contract); the canonical place for provenance is here.
 *
 * Each row shows: filename, chunk index, similarity score, scope
 * (ortak / kişisel) and the snippet excerpt the expert was given.
 * Defaults to OPEN when the speaker is currently active so the
 * operator can see in real time which document the council leans on.
 */
interface RagCitationsSubcardProps {
  batches: TurnRagBatch[];
  defaultOpen?: boolean;
}

const ScopePill = memo(function ScopePill({ scope }: { scope: RagCitation["scope"] }) {
  const isPrivate = scope === "private";
  return (
    <Pill
      tone={isPrivate ? "warn" : "info"}
      className="text-[10px] uppercase tracking-wider"
    >
      {isPrivate ? (
        <span className="inline-flex items-center gap-1">
          <Lock className="h-2.5 w-2.5" aria-hidden /> Kişisel
        </span>
      ) : (
        "Ortak Havuz"
      )}
    </Pill>
  );
});

const CitationRow = memo(function CitationRow({ c }: { c: RagCitation }) {
  // Similarity score is a 0..1 cosine value. We surface it as a percent
  // because a defense audience reads "78% benzerlik" faster than 0.78.
  const pct = Math.round(c.score * 100);
  return (
    <li className="flex items-start gap-3 rounded-md px-2 py-2 transition hover:bg-white/[0.04]">
      <div
        className="mt-0.5 grid h-7 w-7 flex-none place-items-center rounded-md border border-emerald-400/20 bg-emerald-500/10 text-emerald-200"
        aria-hidden
      >
        <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span
            className="truncate text-sm font-medium text-fg-strong"
            title={c.filename}
          >
            {c.filename}
          </span>
          <span className="font-mono text-[10px] tabular-nums text-fg-subtle">
            parça #{c.chunkIdx}
          </span>
          <ScopePill scope={c.scope} />
          <span
            className={cn(
              "font-mono text-[10px] tabular-nums",
              pct >= 75
                ? "text-emerald-300"
                : pct >= 55
                  ? "text-fg-muted"
                  : "text-fg-subtle",
            )}
            title="Sorgu ile pasaj arasındaki cosine benzerlik skoru"
          >
            {pct}% benzerlik
          </span>
        </div>
        {c.excerpt ? (
          <p className="mt-1 line-clamp-3 text-xs leading-snug text-fg-muted">
            "{c.excerpt}"
          </p>
        ) : null}
      </div>
    </li>
  );
});

export const RagCitationsSubcard = memo(function RagCitationsSubcard({
  batches,
  defaultOpen = true,
}: RagCitationsSubcardProps) {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const reduceMotion = useReducedMotion();

  // Flatten across batches and dedupe by (docId, chunkIdx) so a citation
  // that arrived first as `turn.rag` and then re-arrived as part of
  // `turn.end.ragCitations` doesn't render twice.
  const seen = new Set<string>();
  const flat: RagCitation[] = [];
  for (const b of batches) {
    for (const c of b.citations) {
      const key = `${c.docId}#${c.chunkIdx}`;
      if (seen.has(key)) continue;
      seen.add(key);
      flat.push(c);
    }
  }
  flat.sort((a, b) => b.score - a.score);

  const total = flat.length;
  if (total === 0) return null;

  return (
    <section
      className={cn(
        "glass relative flex flex-col gap-1.5 rounded-lg border border-emerald-400/15 px-3 py-2.5",
        "bg-[radial-gradient(circle_at_25%_-20%,hsl(150_70%_55%/0.10),transparent_60%)]",
      )}
      aria-label={`Belge alıntıları (${total})`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="focus-ring flex items-center justify-between gap-3 rounded-md text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs font-medium text-fg-muted">
          <FileText
            className="h-3.5 w-3.5 text-emerald-300"
            strokeWidth={1.5}
            aria-hidden
          />
          <span>
            Belge alıntıları ({total})
            <span className="ml-2 text-[10px] text-fg-subtle">
              yüklenen dokümanlardan dayanak
            </span>
          </span>
        </span>
        <span className="text-fg-subtle">
          {open ? (
            <ChevronDown className="h-4 w-4" strokeWidth={1.5} aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" strokeWidth={1.5} aria-hidden />
          )}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.ul
            key="cits"
            initial={reduceMotion ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="mt-1 flex flex-col gap-0.5 overflow-hidden"
          >
            {flat.map((c, i) => (
              <CitationRow key={`${c.docId}-${c.chunkIdx}-${i}`} c={c} />
            ))}
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </section>
  );
});
